import { useState, useEffect } from 'react';
import { 
  ArrowRight, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Info,
  Calendar,
  FileText,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useFiscalYearTransition } from '../../hooks/useFiscalYearTransition';
import { useCurrency } from '../../hooks/useCurrency';
import type { FiscalYear, TransitionCheck } from '../../types/fiscal-year';

interface TransitionFiscalYearModalProps {
  isOpen: boolean;
  onClose: () => void;
  previousFiscalYear: FiscalYear;
  onSuccess?: (newFiscalYearId: string) => void;
}

export function TransitionFiscalYearModal({
  isOpen,
  onClose,
  previousFiscalYear,
  onSuccess
}: TransitionFiscalYearModalProps) {
  const [step, setStep] = useState<'check' | 'configure' | 'result'>('check');
  const [showDetails, setShowDetails] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    startDate: '',
    endDate: '',
    forceWithoutAudit: false,
    forceWithoutClose: false
  });
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    newFiscalYearId?: string;
    closingEntriesCount?: number;
    openingEntriesCount?: number;
    balances?: {
      assets: number;
      liabilities: number;
      equity: number;
      result: number;
    };
  } | null>(null);

  const { 
    loading, 
    error, 
    transitionState, 
    checkTransition, 
    createNextFiscalYear,
    resetTransitionState,
    hasBlockingErrors,
    hasWarnings
  } = useFiscalYearTransition();
  
  const { formatConverted } = useCurrency();

  // Initialiser les dates du nouvel exercice basé sur l'exercice précédent
  useEffect(() => {
    if (previousFiscalYear && isOpen) {
      const endDate = new Date(previousFiscalYear.endDate);
      const newStartDate = new Date(endDate);
      newStartDate.setDate(newStartDate.getDate() + 1);
      
      const newEndDate = new Date(newStartDate);
      newEndDate.setFullYear(newEndDate.getFullYear() + 1);
      newEndDate.setDate(newEndDate.getDate() - 1);
      
      // Générer le code automatiquement (FY + année)
      const year = newStartDate.getFullYear();
      
      setFormData({
        code: `FY${year}`,
        startDate: newStartDate.toISOString().split('T')[0],
        endDate: newEndDate.toISOString().split('T')[0],
        forceWithoutAudit: false,
        forceWithoutClose: false
      });
    }
  }, [previousFiscalYear, isOpen]);

  // Vérifier la transition quand le modal s'ouvre
  useEffect(() => {
    if (isOpen && previousFiscalYear) {
      checkTransition(previousFiscalYear.id);
      setStep('check');
      setResult(null);
    } else {
      resetTransitionState();
    }
  }, [isOpen, previousFiscalYear, checkTransition, resetTransitionState]);

  const handleCreateNextFiscalYear = async () => {
    const transitionResult = await createNextFiscalYear(formData);
    setResult(transitionResult);
    setStep('result');
    
    if (transitionResult.success && transitionResult.newFiscalYearId && onSuccess) {
      onSuccess(transitionResult.newFiscalYearId);
    }
  };

  const getCheckIcon = (check: TransitionCheck) => {
    if (check.passed) {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
    
    switch (check.severity) {
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  const getCheckBgColor = (check: TransitionCheck) => {
    if (check.passed) {
      return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
    }
    
    switch (check.severity) {
      case 'error':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'warning':
        return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800';
      default:
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
    }
  };

  const renderCheckStep = () => (
    <div className="space-y-4">
      {/* Exercice précédent */}
      <div className="p-4 bg-muted rounded-lg">
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-muted-foreground" />
          <div>
            <p className="font-medium text-foreground">
              Exercice précédent : {previousFiscalYear.code}
            </p>
            <p className="text-sm text-muted-foreground">
              {new Date(previousFiscalYear.startDate).toLocaleDateString('fr-FR')} - {new Date(previousFiscalYear.endDate).toLocaleDateString('fr-FR')}
            </p>
          </div>
          <span className={`ml-auto px-2 py-1 rounded text-xs font-medium ${
            previousFiscalYear.status === 'audited' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
              : previousFiscalYear.status === 'closed'
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
          }`}>
            {previousFiscalYear.status === 'audited' ? 'Audité' : 
             previousFiscalYear.status === 'closed' ? 'Clôturé' : 'Ouvert'}
          </span>
        </div>
      </div>

      {/* État de chargement */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Vérification en cours...</span>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Résultats des vérifications */}
      {!loading && transitionState.checks.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-foreground">Vérifications de transition</h3>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              {showDetails ? 'Masquer' : 'Détails'}
              {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>

          {/* Résumé */}
          <div className={`p-4 rounded-lg border ${
            transitionState.canTransition
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              : hasBlockingErrors()
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
          }`}>
            <div className="flex items-center gap-3">
              {transitionState.canTransition ? (
                <CheckCircle className="h-6 w-6 text-green-500" />
              ) : hasBlockingErrors() ? (
                <XCircle className="h-6 w-6 text-red-500" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-amber-500" />
              )}
              <div>
                <p className="font-medium">
                  {transitionState.canTransition
                    ? 'La transition est possible'
                    : hasBlockingErrors()
                    ? 'La transition n\'est pas possible'
                    : 'La transition est possible avec des avertissements'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {transitionState.checks.filter(c => c.passed).length} / {transitionState.checks.length} vérifications réussies
                </p>
              </div>
            </div>
          </div>

          {/* Détails des vérifications */}
          {showDetails && (
            <div className="space-y-2">
              {transitionState.checks.map((check, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border ${getCheckBgColor(check)}`}
                >
                  <div className="flex items-start gap-3">
                    {getCheckIcon(check)}
                    <div className="flex-1">
                      <p className="font-medium text-sm">{check.name.replace(/_/g, ' ')}</p>
                      <p className="text-sm text-muted-foreground">{check.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="secondary" onClick={onClose}>
          Annuler
        </Button>
        <Button 
          variant="primary"
          icon={ArrowRight}
          onClick={() => setStep('configure')}
          disabled={loading || hasBlockingErrors()}
        >
          Continuer
        </Button>
      </div>
    </div>
  );

  const renderConfigureStep = () => (
    <div className="space-y-4">
      {/* Avertissements si présents */}
      {hasWarnings() && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">Avertissements</p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Certaines conditions ne sont pas remplies. Vous pouvez forcer la transition mais cela n'est pas recommandé.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Code de l'exercice
          </label>
          <input
            type="text"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            placeholder="Ex: FY2025"
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Date de début
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Date de fin
            </label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {/* Options de forçage */}
        {(previousFiscalYear.status !== 'audited' || previousFiscalYear.status === 'open') && (
          <div className="p-4 bg-muted/50 rounded-lg space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Options avancées</p>
            
            {previousFiscalYear.status !== 'audited' && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.forceWithoutAudit}
                  onChange={(e) => setFormData({ ...formData, forceWithoutAudit: e.target.checked })}
                  className="rounded border-input"
                />
                <span className="text-sm">Forcer sans audit (déconseillé)</span>
              </label>
            )}
            
            {previousFiscalYear.status === 'open' && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.forceWithoutClose}
                  onChange={(e) => setFormData({ ...formData, forceWithoutClose: e.target.checked })}
                  className="rounded border-input"
                />
                <span className="text-sm">Forcer sans clôture (déconseillé)</span>
              </label>
            )}
          </div>
        )}
      </div>

      {/* Informations sur ce qui sera fait */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start gap-3">
          <FileText className="h-5 w-5 text-blue-500 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-800 dark:text-blue-200 mb-1">Ce qui sera généré automatiquement :</p>
            <ul className="list-disc list-inside text-blue-700 dark:text-blue-300 space-y-1">
              <li>Écritures de clôture des comptes de résultat (classes 6 et 7)</li>
              <li>Report du résultat vers le compte 13</li>
              <li>Écritures d'ouverture (à-nouveaux) pour les comptes de bilan (classes 1-5)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between gap-3 pt-4 border-t">
        <Button variant="secondary" onClick={() => setStep('check')}>
          Retour
        </Button>
        <Button 
          variant="primary"
          icon={ArrowRight}
          onClick={handleCreateNextFiscalYear}
          disabled={loading || !formData.code || !formData.startDate || !formData.endDate}
        >
          {loading ? 'Création en cours...' : 'Créer l\'exercice'}
        </Button>
      </div>
    </div>
  );

  const renderResultStep = () => (
    <div className="space-y-4">
      {result?.success ? (
        <>
          {/* Succès */}
          <div className="text-center py-6">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Nouvel exercice créé avec succès !
            </h3>
            <p className="text-muted-foreground">
              {result.message}
            </p>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-muted rounded-lg text-center">
              <p className="text-2xl font-bold text-foreground">{result.closingEntriesCount}</p>
              <p className="text-sm text-muted-foreground">Lignes de clôture</p>
            </div>
            <div className="p-4 bg-muted rounded-lg text-center">
              <p className="text-2xl font-bold text-foreground">{result.openingEntriesCount}</p>
              <p className="text-sm text-muted-foreground">Lignes d'ouverture</p>
            </div>
          </div>

          {/* Balances reportées */}
          {result.balances && (
            <div className="p-4 bg-muted/50 rounded-lg">
              <h4 className="font-medium text-foreground mb-3">Soldes reportés</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Actifs</span>
                  <span className="font-medium">{formatConverted(result.balances.assets)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Passifs</span>
                  <span className="font-medium">{formatConverted(result.balances.liabilities)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Capitaux propres</span>
                  <span className="font-medium">{formatConverted(result.balances.equity)}</span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="text-muted-foreground">Résultat de l'exercice</span>
                  <span className={`font-bold ${result.balances.result >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatConverted(result.balances.result)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Erreur */}
          <div className="text-center py-6">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Erreur lors de la création
            </h3>
            <p className="text-red-600 dark:text-red-400">
              {result?.message || error}
            </p>
          </div>
        </>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        {result?.success ? (
          <Button variant="primary" onClick={onClose}>
            Fermer
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Annuler
            </Button>
            <Button variant="primary" onClick={() => setStep('configure')}>
              Réessayer
            </Button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Transition vers un nouvel exercice"
      size="lg"
    >
      <div className="p-4">
        {/* Indicateur d'étapes */}
        <div className="flex items-center justify-center mb-6">
          <div className={`flex items-center ${step === 'check' ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step === 'check' ? 'bg-primary text-white' : 'bg-muted'
            }`}>1</div>
            <span className="ml-2 text-sm hidden sm:inline">Vérification</span>
          </div>
          <div className="w-12 h-px bg-border mx-2" />
          <div className={`flex items-center ${step === 'configure' ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step === 'configure' ? 'bg-primary text-white' : 'bg-muted'
            }`}>2</div>
            <span className="ml-2 text-sm hidden sm:inline">Configuration</span>
          </div>
          <div className="w-12 h-px bg-border mx-2" />
          <div className={`flex items-center ${step === 'result' ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step === 'result' ? 'bg-primary text-white' : 'bg-muted'
            }`}>3</div>
            <span className="ml-2 text-sm hidden sm:inline">Résultat</span>
          </div>
        </div>

        {/* Contenu de l'étape */}
        {step === 'check' && renderCheckStep()}
        {step === 'configure' && renderConfigureStep()}
        {step === 'result' && renderResultStep()}
      </div>
    </Modal>
  );
}
